/**
 * Component: sa-shop-by-category
 * Description: Shop by category section with sliding cards
 */
if (!customElements.get('sa-shop-by-category')) {
  class SaShopByCategory extends HTMLElement {
    constructor() {
      super();
      this.swiperInstance = null;
      this.mobileSwiperInstance = null;
      this.currentActiveIndex = 0;
      this.isSwitchingByClick = false;
      this.mobileNavItems = [];
      this.activeTimeouts = [];
      this.resizeTimeout = null;
      this.initSwiper = this.initSwiper.bind(this);
      this.initMobileSwiper = this.initMobileSwiper.bind(this);
      this.initMobileNav = this.initMobileNav.bind(this);
      this.handleMobileNavClick = this.handleMobileNavClick.bind(this);
      this.scrollNavItemIntoView = this.scrollNavItemIntoView.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.initHoverBlink = this.initHoverBlink.bind(this);
    }
    connectedCallback() {
      if (typeof Swiper === 'undefined') {
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => {
            this.initSwiper();
            this.initMobileSwiper();
            this.initMobileNav();
          });
        } else {
          setTimeout(() => {
            this.initSwiper();
            this.initMobileSwiper();
            this.initMobileNav();
            this.initHoverBlink();
          }, 100);
        }
      } else {
        this.initSwiper();
        this.initMobileSwiper();
        this.initMobileNav();
        this.initHoverBlink();
      }
    }

    disconnectedCallback() {
      if (this.swiperInstance) {
        this.swiperInstance.destroy(true, true);
        this.swiperInstance = null;
      }
      if (this.mobileSwiperInstance) {
        this.mobileSwiperInstance.destroy(true, true);
        this.mobileSwiperInstance = null;
      }
      this.mobileNavItems.forEach((item) => {
        item.removeEventListener('click', this.handleMobileNavClick);
      });
      this.mobileNavItems = [];
      this.activeTimeouts.forEach((timeout) => {
        clearTimeout(timeout);
      });
      this.activeTimeouts = [];
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = null;
      }

      if (this._hoverTargets && this._hoverEnterHandler) {
        this._hoverTargets.forEach((el) => {
          el.removeEventListener('mouseenter', this._hoverEnterHandler);
        });
        this._hoverTargets = [];
        this._hoverEnterHandler = null;
      }
    }

    handleResize() {
      // 预留方法，用于处理窗口 resize 事件
    }

    initSwiper() {
      const swiperContainer = this.querySelector('.sa-shop-by-category__wrapper.swiper');
      if (!swiperContainer || typeof Swiper === 'undefined') {
        return;
      }

      const slidesPerViewDesktopStr = this.getAttribute('data-slides-per-view-desktop');
      const slidesPerViewDesktop = slidesPerViewDesktopStr ? parseFloat(slidesPerViewDesktopStr) : 2.6;
      const slidesPerViewMobile = parseInt(this.getAttribute('data-slides-per-view-mobile')) || 1;
      const spaceBetween = parseInt(this.getAttribute('data-space-between')) || 20;

      const totalSlides = swiperContainer.querySelectorAll('.swiper-slide').length;
      
      const isDesktopInitial = window.innerWidth >= 768;
      
      const swiperOptions = {
        loop: false,
        speed: 600,
        slidesPerView: slidesPerViewMobile,
        spaceBetween: spaceBetween,
        slidesPerGroup: 1,
        grabCursor: true,
        allowTouchMove: true,
        watchOverflow: true,
        mousewheel: {
          enabled: isDesktopInitial,
          forceToAxis: true,
          sensitivity: 1,
          releaseOnEdges: false,
        },
        navigation: {
          nextEl: swiperContainer.querySelector('.swiper-button-next'),
          prevEl: swiperContainer.querySelector('.swiper-button-prev'),
          hideOnClick: false,
        },
        breakpoints: {
          0: {
            slidesPerView: slidesPerViewMobile,
            spaceBetween: spaceBetween / 2,
            mousewheel: {
              enabled: false,
            },
          },
          768: {
            slidesPerView: slidesPerViewDesktop,
            spaceBetween: spaceBetween,
            mousewheel: {
              enabled: true,
              forceToAxis: true,
              sensitivity: 1,
              releaseOnEdges: false,
            },
          },
        },
        on: {
          init: (swiper) => {
            swiperContainer.classList.add('swiper-initialized');
            this.updateNavigationVisibility(swiper, totalSlides, slidesPerViewDesktop, slidesPerViewMobile);
          },
          resize: (swiper) => {
            // 防抖处理，避免频繁触发导致闪烁
            if (this.resizeTimeout) {
              clearTimeout(this.resizeTimeout);
            }
            this.resizeTimeout = setTimeout(() => {
              const isDesktop = window.innerWidth >= 768;
              const currentSlidesPerView = isDesktop ? slidesPerViewDesktop : slidesPerViewMobile;
              this.updateNavigationVisibility(swiper, totalSlides, currentSlidesPerView, currentSlidesPerView);
            }, 150);
          },
        },
      };

      this.swiperInstance = new Swiper(swiperContainer, swiperOptions);
    }

    updateNavigationVisibility(swiper, totalSlides, slidesPerViewDesktop, slidesPerViewMobile) {
      const isDesktop = window.innerWidth >= 768;
      const currentSlidesPerView = isDesktop ? slidesPerViewDesktop : slidesPerViewMobile;
      const needsNavigation = totalSlides > currentSlidesPerView;
      
      const nextBtn = swiper.el.querySelector('.swiper-button-next');
      const prevBtn = swiper.el.querySelector('.swiper-button-prev');
      
      if (nextBtn && prevBtn) {
        if (needsNavigation) {
          nextBtn.style.display = '';
          prevBtn.style.display = '';
        } else {
          nextBtn.style.display = 'none';
          prevBtn.style.display = 'none';
        }
      }
    }

    initMobileNav() {
      if (window.innerWidth >= 768) return;

      const navItems = this.querySelectorAll('.sa-shop-by-category__mobile-nav-item');
      this.mobileNavItems = Array.from(navItems);

      this.mobileNavItems.forEach((item) => {
        item.addEventListener('click', this.handleMobileNavClick);
      });
    }

    initMobileSwiper() {
      if (window.innerWidth >= 768) return;

      const mobileSwiperContainer = this.querySelector('.sa-shop-by-category__mobile-main-image');
      if (!mobileSwiperContainer || typeof Swiper === 'undefined') {
        return;
      }

      const mobileSwiperOptions = {
        loop: false,
        speed: 400,
        slidesPerView: 1,
        spaceBetween: 0,
        allowTouchMove: true,
        grabCursor: true,
        on: {
          slideChange: (swiper) => {
            const activeIndex = swiper.activeIndex;
            if (activeIndex !== this.currentActiveIndex && !this.isSwitchingByClick) {
              this.switchToBlock(activeIndex, true);
            }
          },
        },
      };

      this.mobileSwiperInstance = new Swiper(mobileSwiperContainer, mobileSwiperOptions);
    }

    initHoverBlink() {
      const imageWrappers = Array.from(this.querySelectorAll('.sa-shop-by-category__image-wrapper'));
      if (imageWrappers.length === 0) return;

      const handleEnter = (e) => {
        if (window.innerWidth < 768) return;
        const wrapper = e.currentTarget;
        const card = wrapper.closest('.sa-shop-by-category__card');
        if (!card) return;
        const titleEl = card.querySelector('.sa-shop-by-category__title');
        if (!titleEl) return;
        titleEl.classList.remove('sa-shop-by-category__title--blink');
        void titleEl.offsetWidth;
        titleEl.classList.add('sa-shop-by-category__title--blink');
        const timeout = setTimeout(() => {
          titleEl.classList.remove('sa-shop-by-category__title--blink');
        }, 1100);
        this.activeTimeouts.push(timeout);
      };

      imageWrappers.forEach((el) => {
        el.addEventListener('mouseenter', handleEnter);
      });

      this._hoverTargets = imageWrappers;
      this._hoverEnterHandler = handleEnter;
    }

    handleMobileNavClick(e) {
      e.preventDefault();
      const button = e.currentTarget;
      const blockIndex = parseInt(button.getAttribute('data-block-index'));

      if (blockIndex === this.currentActiveIndex) return;

      this.isSwitchingByClick = true;
      if (this.mobileSwiperInstance) {
        this.mobileSwiperInstance.slideTo(blockIndex);
      }
      this.switchToBlock(blockIndex, false);
      const timeout = setTimeout(() => {
        this.isSwitchingByClick = false;
      }, 600);
      this.activeTimeouts.push(timeout);
    }

  scrollNavItemIntoView(navItem) {
      const navContainer = this.querySelector('.sa-shop-by-category__mobile-nav');
      if (!navContainer || !navItem) return;

      // Only adjust on mobile
      if (window.innerWidth >= 768) return;

      const navWrapper = this.querySelector('.sa-shop-by-category__mobile-nav-wrapper');
      if (!navWrapper) return;

      const containerRect = navContainer.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const paddingLeft = parseInt(getComputedStyle(navContainer).paddingLeft || '0', 10) || 0;
      const safeMargin = 10; // small breathing room so it doesn't glue to the edge

      // Align active item near the left edge
      let targetLeft = navItem.offsetLeft - paddingLeft - safeMargin;

      // Clamp within scroll bounds
      const maxScroll = Math.max(0, navWrapper.scrollWidth - containerWidth);
      targetLeft = Math.max(0, Math.min(targetLeft, maxScroll));

      navContainer.scrollTo({ left: targetLeft, behavior: 'smooth' });
    }

    switchToBlock(index, isSwipeTransition = false) {
      const navItems = this.mobileNavItems.length > 0 
        ? this.mobileNavItems 
        : Array.from(this.querySelectorAll('.sa-shop-by-category__mobile-nav-item'));
      const imageItems = Array.from(this.querySelectorAll('.sa-shop-by-category__mobile-image-item'));

      if (navItems.length === 0 || imageItems.length === 0) return;

      this.currentActiveIndex = index;

      navItems.forEach((item, i) => {
        if (i === index) {
          item.classList.add('sa-shop-by-category__mobile-nav-item--active');
          const titleEl = item.querySelector('.sa-shop-by-category__mobile-nav-title');
          if (titleEl) {
            titleEl.classList.remove('sa-shop-by-category__mobile-nav-title--blink');
            const timeout1 = setTimeout(() => {
              titleEl.classList.add('sa-shop-by-category__mobile-nav-title--blink');
              const timeout2 = setTimeout(() => {
                titleEl.classList.remove('sa-shop-by-category__mobile-nav-title--blink');
              }, 600);
              this.activeTimeouts.push(timeout2);
            }, 10);
            this.activeTimeouts.push(timeout1);
          }
          const scrollTimeout = setTimeout(() => {
            this.scrollNavItemIntoView(item);
          }, 50);
          this.activeTimeouts.push(scrollTimeout);
        } else {
          item.classList.remove('sa-shop-by-category__mobile-nav-item--active');
          const titleEl = item.querySelector('.sa-shop-by-category__mobile-nav-title');
          if (titleEl) {
            titleEl.classList.remove('sa-shop-by-category__mobile-nav-title--blink');
          }
        }
      });

      imageItems.forEach((item, i) => {
        const imgEl = item.querySelector('.sa-shop-by-category__mobile-main-img');
        const placeholderEl = item.querySelector('.sa-shop-by-category__mobile-placeholder');
        
        if (i === index) {
          item.classList.add('sa-shop-by-category__mobile-image-item--active');
          if (isSwipeTransition) {
            if (imgEl) {
              imgEl.classList.add('sa-shop-by-category__mobile-main-img--zoom');
              const zoomTimeout = setTimeout(() => {
                imgEl.classList.remove('sa-shop-by-category__mobile-main-img--zoom');
              }, 400);
              this.activeTimeouts.push(zoomTimeout);
            } else if (placeholderEl) {
              placeholderEl.classList.add('sa-shop-by-category__mobile-placeholder--zoom');
              const placeholderTimeout = setTimeout(() => {
                placeholderEl.classList.remove('sa-shop-by-category__mobile-placeholder--zoom');
              }, 400);
              this.activeTimeouts.push(placeholderTimeout);
            }
          }
        } else {
          item.classList.remove('sa-shop-by-category__mobile-image-item--active');
          if (imgEl) {
            imgEl.classList.remove('sa-shop-by-category__mobile-main-img--zoom');
          }
          if (placeholderEl) {
            placeholderEl.classList.remove('sa-shop-by-category__mobile-placeholder--zoom');
          }
        }
      });
    }
  }

  customElements.define('sa-shop-by-category', SaShopByCategory);
}
